import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import * as React from "react";
import { useNavigate } from "react-router";
import { browserState } from "~/config/auth0/browser-state";
import { env } from "~/config/env";

/**
 * Will create a native app compatible url APP_ID://path/url
 * @param path patht to navigate to (like: /project/something)
 * @returns
 */
function buildNativeSchemeUrl(path: string) {
  return `${env.VITE_NATIVE_APP_ID}:/${path}`;
}

/**
 * Will take a native app url like APP_ID://path/url
 * and turn it into a /path/url compatible with our app router
 * @param href Native app scheme url
 * @returns
 */
function nativeSchemeUrlToRelative(href: string) {
  return href.replace(`${env.VITE_NATIVE_APP_ID}:/`, "");
}

function isAuthCallbackUrl(url: string) {
  return (
    url.includes("code=") || url.includes("state=") || url.includes("error=")
  );
}

/**
 * On iOS, closing the SFSafariViewController (used by Auth0 / Capacitor Browser)
 * fires an appUrlOpen with the app's redirect URI, e.g. "dev.clovis.app://localhost".
 * This is NOT a real deep link — it's just iOS returning control to the app.
 * Navigating to it re-renders the Auth0Provider and causes an infinite login loop.
 */
function isAppBaseRedirectUri(url: string) {
  try {
    const parsed = new URL(url);
    // APP_ID://localhost  or  APP_ID://localhost/
    const isBaseUri =
      parsed.hostname === "localhost" &&
      (parsed.pathname === "/" || parsed.pathname === "") &&
      !parsed.search;
    return isBaseUri;
  } catch {
    return false;
  }
}

const AppUrlListener: React.FC = (props) => {
  const navigate = useNavigate();
  const isNativePlatform = Capacitor.isNativePlatform();

  React.useEffect(() => {
    if (Capacitor.isPluginAvailable("App") && isNativePlatform) {
      void App.getLaunchUrl().then((launchUrl) => {
        if (
          launchUrl?.url &&
          !launchUrl.url.includes("callback") &&
          !isAuthCallbackUrl(launchUrl.url)
        ) {
          if (launchUrl.url.includes(`${env.VITE_NATIVE_APP_ID}://`)) {
            const replacedAppScheme = nativeSchemeUrlToRelative(launchUrl.url);
            navigate(replacedAppScheme);
          } else {
            const url = new URL(launchUrl.url);
            navigate(url);
          }
        }
      });
      void App.addListener("appUrlOpen", (event) => {
        // doesn't have anything to do with our Auth0 login logic
        if (
          event?.url &&
          !event.url.includes("callback") &&
          !isAuthCallbackUrl(event.url)
        ) {
          // Skip navigation while the auth browser is open.
          if (browserState.getIsOpen()) {
            return;
          }

          // On iOS, closing the SFSafariViewController fires an appUrlOpen
          // with the app's base redirect URI (e.g. "APP_ID://localhost").
          // This is not a real deep link — navigating to it would re-render
          // the Auth0Provider and restart the auth flow, causing an infinite
          // login loop.
          if (isAppBaseRedirectUri(event.url)) {
            return;
          }

          const url = new URL(event.url);
          navigate(url);
        }
      });
    }
  }, [isNativePlatform, navigate]);
  return <>{props.children}</>;
};

export { AppUrlListener, buildNativeSchemeUrl };
